import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useBranch } from '@/context/BranchContext';
import { formatMoney, formatDateTime } from '@/lib/format';
import { friendlyError } from '@/lib/errors';
import {
  CASH_SESSION_STATUS_LABELS,
  formatPeriodLabel,
} from '@/lib/cashSession';
import { printCashSession } from '@/services/print/printCashSession';
import Segmented from '@/components/base/Segmented';
import Modal from '@/components/base/Modal';
import type { CashSession } from '@/lib/types';

type StatusFilter = 'all' | 'open' | 'closed';

interface ProfileLite {
  id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
}

export default function CashSupervisionPage() {
  const { activeBranch, activeBranchId } = useBranch();
  const [sessions, setSessions] = useState<CashSession[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateFilter, setDateFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CashSession | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('cash_sessions')
        .select('*, branch:branches(name)')
        .order('period_date', { ascending: false })
        .limit(200);
      if (activeBranchId) query = query.eq('branch_id', activeBranchId);
      const { data, error: e } = await query;
      if (e) throw e;
      const rows = (data as CashSession[]) ?? [];
      setSessions(rows);

      const ids = [...new Set(rows.map((r) => r.user_id))];
      if (ids.length > 0) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('id, role, first_name, last_name')
          .in('id', ids);
        const map: Record<string, ProfileLite> = {};
        ((profs as ProfileLite[]) ?? []).forEach((p) => {
          map[p.id] = p;
        });
        setProfiles(map);
      } else {
        setProfiles({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los cuadres');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (dateFilter && s.period_date !== dateFilter) return false;
      return true;
    });
  }, [sessions, statusFilter, dateFilter]);

  const roleLabel = (role?: string) =>
    role === 'cashier' ? 'Cajero' : role ? 'Administrador' : '—';

  const nameOf = (s: CashSession) => {
    const p = profiles[s.user_id];
    if (p) {
      return (
        [p.first_name, p.last_name].filter(Boolean).join(' ') || s.user_name || 'Usuario'
      );
    }
    return s.user_name || 'Usuario';
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Cuadres
          </h1>
          <p className="mt-1 text-foreground-600">
            Supervisa los cuadres de caja de los usuarios
            {activeBranch ? ` de ${activeBranch.name}` : ' de todas las sucursales'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-refresh-line" /> Actualizar
          </button>
          <Link
            to="/cash"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-3 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-wallet-3-line" /> Mi cuadre
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'open', label: 'Abiertos' },
            { value: 'closed', label: 'Cerrados' },
          ]}
        />
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-6 text-center">
          <i className="ri-error-warning-line text-4xl text-accent-700" />
          <p className="mt-3 text-foreground-800">{friendlyError(error, 'No se pudieron cargar los cuadres.')}</p>
          <button
            onClick={load}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-refresh-line" /> Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-folder-shield-2-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">No hay cuadres que coincidan con el filtro.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Usuario</th>
                <th className="px-4 py-3 font-medium">Rol</th>
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Sucursal</th>
                <th className="px-4 py-3 font-medium text-center">Ventas</th>
                <th className="px-4 py-3 font-medium text-right">Total</th>
                <th className="px-4 py-3 font-medium text-right">Efvo. esp.</th>
                <th className="px-4 py-3 font-medium text-right">Diferencia</th>
                <th className="px-4 py-3 font-medium text-center">Estado</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {filtered.map((s) => {
                const diff = s.difference != null ? Number(s.difference) : null;
                return (
                  <tr key={s.id} className="hover:bg-background-100/60 transition">
                    <td className="px-4 py-3 font-medium text-foreground-950">{nameOf(s)}</td>
                    <td className="px-4 py-3 text-foreground-700">
                      {roleLabel(profiles[s.user_id]?.role ?? s.user_role ?? undefined)}
                    </td>
                    <td className="px-4 py-3 text-foreground-700">{formatPeriodLabel(s.period_date)}</td>
                    <td className="px-4 py-3 text-foreground-700">{s.branch?.name || '—'}</td>
                    <td className="px-4 py-3 text-center text-foreground-700">{s.sales_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground-950">
                      {formatMoney(Number(s.total), 'RD$')}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground-700">
                      {formatMoney(Number(s.cash_expected), 'RD$')}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-medium ${
                        diff != null && Math.abs(diff) >= 0.005 ? 'text-accent-700' : 'text-foreground-700'
                      }`}
                    >
                      {diff == null ? '—' : formatMoney(diff, 'RD$')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                          s.status === 'closed'
                            ? 'bg-secondary-100 text-secondary-900'
                            : 'bg-accent-100 text-accent-900'
                        }`}
                      >
                        {CASH_SESSION_STATUS_LABELS[s.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setDetail(s)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-primary-700 hover:bg-primary-50 cursor-pointer"
                          title="Ver detalle"
                        >
                          <i className="ri-eye-line" />
                        </button>
                        <button
                          onClick={() => printCashSession({ branch: activeBranch, session: s })}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
                          title="Imprimir cuadre"
                        >
                          <i className="ri-printer-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={detail != null}
        onClose={() => setDetail(null)}
        title="Detalle del cuadre"
      >
        {detail && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-foreground-500">Responsable</p>
                <p className="font-medium text-foreground-950">{nameOf(detail)}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Rol</p>
                <p className="font-medium text-foreground-950">
                  {roleLabel(profiles[detail.user_id]?.role ?? detail.user_role ?? undefined)}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Sucursal</p>
                <p className="font-medium text-foreground-950">{detail.branch?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Fecha</p>
                <p className="font-medium text-foreground-950">{formatPeriodLabel(detail.period_date)}</p>
              </div>
            </div>

            <div className="rounded-md border border-background-200 divide-y divide-background-100">
              {(detail.payment_breakdown ?? []).map((r) => (
                <div key={r.method} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-foreground-700">
                    {r.label} ({r.count})
                  </span>
                  <span className="font-medium text-foreground-950">{formatMoney(Number(r.total), 'RD$')}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="font-medium text-foreground-800">Total vendido</span>
                <span className="font-heading font-bold text-foreground-950">
                  {formatMoney(Number(detail.total), 'RD$')}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-foreground-700">Efectivo esperado</span>
                <span className="font-medium text-foreground-950">
                  {formatMoney(Number(detail.cash_expected), 'RD$')}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-foreground-700">Efectivo contado</span>
                <span className="font-medium text-foreground-950">
                  {detail.cash_counted == null ? '—' : formatMoney(Number(detail.cash_counted), 'RD$')}
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-foreground-700">Diferencia</span>
                <span className="font-medium text-foreground-950">
                  {detail.difference == null ? '—' : formatMoney(Number(detail.difference), 'RD$')}
                </span>
              </div>
            </div>

            {detail.observations && (
              <p className="text-sm text-foreground-700">
                <span className="text-foreground-500">Observación: </span>
                {detail.observations}
              </p>
            )}
            {detail.closed_at && (
              <p className="text-xs text-foreground-500">Cerrado: {formatDateTime(detail.closed_at)}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}